import { createTheme, alpha } from "@mui/material/styles";

export const reviewTokens = {
  brand: "#F58220",
  brandHover: "#E8722E",
  brandSoft: "#FFF1E0",

  slate50: "#F8FAFC",
  slate100: "#F1F5F9",
  slate200: "#E5E7EB",
  slate300: "#CBD5E1",
  slate400: "#94A3B8",
  slate500: "#64748B",
  slate600: "#475569",
  slate700: "#334155",
  slate900: "#0F172A",

  success: "#16A34A",
  warning: "#F59E0B",
  info: "#0EA5E9",
  danger: "#DC2626",

  bgPage: "#FAFAFA",
  bgPaper: "#FFFFFF",
  bgMuted: "#F8FAFC",
  border: "#E5E7EB",
  borderStrong: "#CBD5E1",

  textPrimary: "#0F172A",
  textSecondary: "#64748B",
  textMuted: "#94A3B8",

  fontFamily:
    '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
  fontMono:
    'ui-monospace, SFMono-Regular, "SF Mono", Menlo, Consolas, "Liberation Mono", monospace',

  radiusSm: 6,
  radiusMd: 8,
  radiusLg: 10,
};

export const reviewTheme = createTheme({
  palette: {
    mode: "light",
    primary: {
      main: reviewTokens.brand,
      dark: reviewTokens.brandHover,
      light: reviewTokens.brandSoft,
      contrastText: "#FFFFFF",
    },
    success: { main: reviewTokens.success },
    warning: { main: reviewTokens.warning },
    info: { main: reviewTokens.info },
    error: { main: reviewTokens.danger },
    background: {
      default: reviewTokens.bgPage,
      paper: reviewTokens.bgPaper,
    },
    text: {
      primary: reviewTokens.textPrimary,
      secondary: reviewTokens.textSecondary,
      disabled: reviewTokens.textMuted,
    },
    divider: reviewTokens.border,
    action: {
      hover: reviewTokens.slate50,
      selected: reviewTokens.slate100,
    },
  },
  shape: {
    borderRadius: reviewTokens.radiusMd,
  },
  typography: {
    fontFamily: reviewTokens.fontFamily,
    h6: {
      fontSize: "1rem",
      fontWeight: 600,
      letterSpacing: "-0.01em",
      lineHeight: 1.4,
    },
    subtitle1: {
      fontSize: "0.9375rem",
      fontWeight: 600,
      letterSpacing: "-0.005em",
    },
    subtitle2: {
      fontSize: "0.6875rem",
      fontWeight: 600,
      letterSpacing: "0.08em",
      textTransform: "uppercase",
      color: reviewTokens.textSecondary,
    },
    body1: {
      fontSize: "0.875rem",
      lineHeight: 1.55,
    },
    body2: {
      fontSize: "0.8125rem",
      lineHeight: 1.55,
    },
    caption: {
      fontSize: "0.75rem",
      color: reviewTokens.textSecondary,
    },
    button: {
      textTransform: "none",
      fontWeight: 500,
      letterSpacing: 0,
    },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundColor: reviewTokens.bgPage,
        },
      },
    },
    MuiPaper: {
      defaultProps: { elevation: 0 },
      styleOverrides: {
        root: {
          backgroundImage: "none",
        },
        outlined: {
          borderColor: reviewTokens.border,
          borderRadius: reviewTokens.radiusLg,
        },
      },
    },
    MuiCard: {
      defaultProps: { variant: "outlined", elevation: 0 },
      styleOverrides: {
        root: {
          borderColor: reviewTokens.border,
          borderRadius: reviewTokens.radiusLg,
        },
      },
    },
    MuiCardContent: {
      styleOverrides: {
        root: {
          padding: 16,
          "&:last-child": { paddingBottom: 16 },
        },
      },
    },
    MuiButton: {
      defaultProps: {
        disableElevation: true,
      },
      styleOverrides: {
        root: {
          borderRadius: reviewTokens.radiusMd,
          fontWeight: 500,
        },
        sizeSmall: {
          minHeight: 28,
          paddingTop: 4,
          paddingBottom: 4,
        },
        containedPrimary: {
          "&:hover": { backgroundColor: reviewTokens.brandHover },
        },
        outlined: {
          borderColor: reviewTokens.border,
          color: reviewTokens.textPrimary,
          "&:hover": {
            borderColor: reviewTokens.borderStrong,
            backgroundColor: reviewTokens.slate50,
          },
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          borderRadius: reviewTokens.radiusSm,
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: reviewTokens.radiusSm,
          fontWeight: 500,
          letterSpacing: 0,
        },
        sizeSmall: {
          height: 20,
          fontSize: "0.6875rem",
        },
        outlined: {
          borderColor: reviewTokens.border,
        },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          borderRadius: 0,
          "&:hover": { backgroundColor: reviewTokens.slate50 },
          "&.Mui-selected": {
            backgroundColor: reviewTokens.slate50,
            "&:hover": { backgroundColor: reviewTokens.slate100 },
          },
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        root: {
          minHeight: 36,
        },
        indicator: {
          height: 2,
          borderRadius: 1,
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          minHeight: 36,
          textTransform: "none",
          fontWeight: 500,
          fontSize: "0.8125rem",
          padding: "6px 12px",
          color: reviewTokens.textSecondary,
          "&.Mui-selected": { color: reviewTokens.textPrimary },
        },
      },
    },
    MuiDivider: {
      styleOverrides: {
        root: {
          borderColor: reviewTokens.border,
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: reviewTokens.radiusMd,
          backgroundColor: reviewTokens.bgPaper,
          "& .MuiOutlinedInput-notchedOutline": {
            borderColor: reviewTokens.border,
          },
          "&:hover .MuiOutlinedInput-notchedOutline": {
            borderColor: reviewTokens.borderStrong,
          },
          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
            borderColor: reviewTokens.brand,
            borderWidth: 1,
          },
        },
      },
    },
    MuiAvatar: {
      styleOverrides: {
        root: {
          fontSize: "0.8125rem",
          fontWeight: 600,
        },
        rounded: {
          borderRadius: reviewTokens.radiusSm,
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: reviewTokens.slate900,
          fontSize: "0.6875rem",
          fontWeight: 500,
          borderRadius: reviewTokens.radiusSm,
          padding: "6px 8px",
        },
      },
    },
  },
});

export const reviewSurfaceShadow = `0 1px 2px ${alpha(reviewTokens.slate900, 0.04)}`;
